import { FIELDS, PRIORITY_LEVELS, VALIDATION_TYPES } from '../constants.js'
import createValidator from './createValidator.js'
import createWrongAddressTypeValidator from './createWrongAddressTypeValidator.js'

// cannot send SOL to token Address
export const solanaAddressTypeValidator = createWrongAddressTypeValidator({
  shouldValidate: ({ asset }) => ['solana'].includes(asset.name),
  getMessage: () => 'Destination address is for a different token type.',
})

// cannot send token X to a different target account address (token Y)
export const solanaMintAddressValidator = createValidator({
  id: 'ADDRESS_MINT_MISMATCH',
  type: VALIDATION_TYPES.ERROR,
  shouldValidate: ({ asset }) => ['SOLANA_TOKEN'].includes(asset.assetType),

  isValid: async ({ asset, addressDetails }) =>
    !addressDetails.targetMint || asset.mintAddress === addressDetails.targetMint,
  priority: PRIORITY_LEVELS.MIDDLE,
  getMessage: ({ asset }) =>
    `Destination Wallet is not a ${asset.baseAsset.displayName} ${asset.displayTicker} address.`,
  field: FIELDS.ADDRESS,
})

export const solanaAddressValidator = createValidator({
  id: 'SOLANA_ADDRESS',
  type: VALIDATION_TYPES.ERROR,
  shouldValidate: ({ asset }) => asset.name === 'solana',
  isValid: async ({ addressDetails }) => {
    return addressDetails.addressType !== 'token'
  },
  priority: PRIORITY_LEVELS.MIDDLE,
  getMessage: () => `The Solana network doesn't allow sending SOL to a Token Account address.`,
  field: FIELDS.ADDRESS,
})

export const solanaRentExemptAmountValidator = createValidator({
  id: 'SOL_RENT_EXEMPT_AMOUNT',
  type: VALIDATION_TYPES.ERROR,
  shouldValidate: ({ asset, sendAmount }) =>
    sendAmount && (asset.assetType === 'SOLANA_TOKEN' || asset.name === 'solana'),
  isValid: async ({ asset, destinationAddress, sendAmount, baseAssetBalance, feeAmount }) => {
    if (!destinationAddress) {
      return true
    }

    const serverApi = asset.baseAsset.serverApi
    const rentExemptValue = await serverApi.getRentExemptionMinAmount(destinationAddress)
    const rentExemptAmount = asset.baseAsset.currency.baseUnit(rentExemptValue)

    // differentiate between SOL and Solana token
    let isEnoughForRent
    if (asset.name === asset.baseAsset.name) {
      // sending SOL
      isEnoughForRent = sendAmount.gte(rentExemptAmount)
    } else {
      // sending token
      isEnoughForRent = baseAssetBalance
        .sub(feeAmount || asset.feeAsset.currency.ZERO)
        .gte(rentExemptAmount)
    }

    return isEnoughForRent
  },
  priority: PRIORITY_LEVELS.MIDDLE,
  getMessage: ({ asset: { baseAsset } }) =>
    `Your sending ${baseAsset.displayTicker} amount is too low to cover the rent exemption fee.`,
  field: FIELDS.ADDRESS,
})

export const solanaRentExemptAmountSenderValidator = {
  id: 'SOL_RENT_EXEMPT_AMOUNT_SENDER',
  type: VALIDATION_TYPES.ERROR,
  priority: PRIORITY_LEVELS.BASE,
  shouldValidate: ({ asset, sendAmount, fees, spendableBalance, accountState }) =>
    sendAmount &&
    asset.name === 'solana' &&
    fees &&
    accountState?.rentExemptAmount &&
    spendableBalance,

  isValid: ({ accountState, asset, fromAddress, sendAmount, spendableBalance, fees }) => {
    const rentExemptAmount = accountState.rentExemptAmount
    const remaining = spendableBalance.sub(fees.fee).sub(sendAmount)
    return remaining.isZero || remaining.gte(rentExemptAmount)
  },

  getMessage: ({ accountState, asset }) =>
    `You can either leave a zero balance, which will close your SOL account, or maintain a minimum balance of ${accountState.rentExemptAmount.toDefaultString()} ${asset.displayTicker} to keep it active.`,
}

export const solanaPayValidator = createValidator({
  id: 'SOLANA_PAY',
  type: VALIDATION_TYPES.ERROR,
  shouldValidate: ({ solanaPayInfo }) => {
    return !!(solanaPayInfo?.recipient || solanaPayInfo?.link)
  },
  isValid: async ({ solanaPayInfo }) => {
    return !(solanaPayInfo.recipient || solanaPayInfo.link)
  },
  priority: PRIORITY_LEVELS.MIDDLE,
  getMessage: () => `Please use Solana Pay feature to scan this QRCode.`,
  field: FIELDS.ADDRESS,
})
